import { useState, useEffect } from "react";
import { fileUrl } from "@/lib/api";
import { cn } from "@/lib/utils";
import { UserRound, Crown } from "lucide-react";

export function Avatar({ src, name, size = 40, className }) {
  const url = fileUrl(src);
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [url]);
  const initials = (name || "")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div
      className={cn(
        "rounded-full overflow-hidden bg-secondary grid place-items-center text-xs font-semibold text-muted-foreground shrink-0 ring-1 ring-border/60",
        className
      )}
      style={{ width: size, height: size }}
    >
      {url && !broken ? (
        <img
          src={url}
          alt={name || "avatar"}
          className="h-full w-full object-cover"
          onError={() => setBroken(true)}
        />
      ) : initials ? (
        initials
      ) : (
        <UserRound className="h-1/2 w-1/2" />
      )}
    </div>
  );
}

export function Tag({ children, tone = "muted", className, ...rest }) {
  const tones = {
    muted: "bg-secondary text-muted-foreground ring-border/60",
    green: "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30",
    red: "bg-red-500/15 text-red-300 ring-red-500/30",
    amber: "bg-amber-500/15 text-amber-300 ring-amber-500/30",
    primary: "bg-primary/20 text-primary-foreground ring-primary/40",
    blue: "bg-sky-500/15 text-sky-300 ring-sky-500/30",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1",
        tones[tone],
        className
      )}
      {...rest}
    >
      {children}
    </span>
  );
}

export function EmptyState({ icon: Icon, title, hint }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      {Icon && (
        <div className="h-14 w-14 rounded-2xl bg-secondary grid place-items-center mb-4">
          <Icon className="h-7 w-7 text-muted-foreground" />
        </div>
      )}
      <p className="font-display text-lg">{title}</p>
      {hint && <p className="text-sm text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

export function Spinner({ className }) {
  return (
    <div className={cn("flex items-center justify-center py-20", className)}>
      <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
    </div>
  );
}

export function formatDate(iso, opts) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, opts || { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

const PLAN_LABEL = { weekly: "Weekly", monthly: "Monthly", yearly: "Yearly", custom: "Custom" };

// Premium status badge: "Free", "Premium · <plan>", with expiry sub-line if present.
export function PremiumBadge({ user }) {
  if (!user?.premium) return <Tag data-testid="premium-badge">Free</Tag>;
  const plan = user.premium_plan ? PLAN_LABEL[user.premium_plan] || user.premium_plan : null;
  const exp = user.premium_expires_at ? formatDate(user.premium_expires_at) : null;
  return (
    <span className="inline-flex flex-col items-start gap-0.5" data-testid="premium-badge">
      <Tag tone="amber">
        <Crown className="h-3 w-3" />
        Premium{plan ? ` · ${plan}` : ""}
      </Tag>
      {exp && (
        <span className="text-[10px] text-muted-foreground" data-testid="premium-expiry">
          expires {exp}
        </span>
      )}
    </span>
  );
}
